import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Linkedin, Twitter, Mail, ExternalLink } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    product: [
      { label: 'Screener', path: '/screener' },
      { label: 'Marchés', path: '/markets' },
      { label: 'Tarifs', path: '/pricing' },
      { label: 'Guides', path: '/learn' },
    ],
    markets: [
      { label: 'Vietnam', path: '/markets/vn' },
      { label: 'Inde', path: '/markets/nse' },
      { label: 'Philippines', path: '/markets/pse' },
      { label: 'Indonésie', path: '/markets/jk' },
      { label: 'Pologne', path: '/markets/wse' },
      { label: 'Corée du Sud', path: '/markets/krx' },
    ],
    company: [
      { label: 'À propos', path: '/about' },
      { label: 'Blog', path: '/blog' },
      { label: 'Contact', path: '/contact' },
    ],
    legal: [
      { label: 'Conditions générales de vente', path: '/cgv' },
      { label: 'Conditions d\'utilisation', path: '/terms' },
      { label: 'Politique de confidentialité', path: '/privacy' },
      { label: 'Mentions légales', path: '/legal' },
    ],
  };

  return (
    <footer className="bg-[#080c14] border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center space-x-2 mb-4">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white">
                VALOR<span className="text-emerald-400">AMAP</span>
              </span>
            </Link>
            <p className="text-sm text-gray-400 mb-4">
              Le premier screener value francophone dédié aux marchés émergents à croissance explosive.
            </p>
            <div className="flex items-center space-x-3">
              <a 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a 
                href="https://twitter.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
              >
                <Twitter className="w-4 h-4" />
              </a>
              <a 
                href="mailto:contact@alainponcelas.com"
                className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
              >
                <Mail className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">Produit</h4>
            <ul className="space-y-2">
              {footerLinks.product.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-sm text-gray-400 hover:text-emerald-400 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Markets Links */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">Marchés</h4>
            <ul className="space-y-2">
              {footerLinks.markets.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-sm text-gray-400 hover:text-emerald-400 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">Entreprise</h4>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-sm text-gray-400 hover:text-emerald-400 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">Légal</h4>
            <ul className="space-y-2">
              {footerLinks.legal.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-sm text-gray-400 hover:text-emerald-400 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-500">
              © 2026 Scalabla Swiss Sàrl. Tous droits réservés.
            </p>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <span className="flex items-center">
                <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2 animate-pulse"></span>
                Données mises à jour quotidiennement
              </span>
            </div>
          </div>
          <p className="mt-4 text-xs text-gray-600 text-center md:text-left">
            Avertissement : Les informations fournies ne constituent pas des conseils en investissement. 
            Les performances passées ne préjugent pas des performances futures. Investir comporte des risques de perte en capital.
          </p>
          {/* Version hash */}
          <p className="mt-2 text-xs text-gray-500 opacity-50 text-center md:text-right">
            v: {process.env.REACT_APP_GIT_SHA || 'dev'}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
